// src/components/PrivacyPolicy.js
import React from "react";

const sections = [
  {
    id: "intro",
    title: "Introduction",
    icon: "✦",
    content: `Nairobi's Ultimate Experience City Tours & Adventure ("we," "our," or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or book one of our tours. Please read this policy carefully. If you disagree with its terms, please discontinue use of our services.`,
  },
  {
    id: "collect",
    title: "Information We Collect",
    icon: "✦",
    content: `We collect information you provide directly when you:\n\n• Make a booking or enquiry\n• Register for an account\n• Contact us via email, phone, or WhatsApp\n• Submit a review or feedback\n\nThis may include your name, email address, phone number, nationality, and payment details. We may also collect non-personal data such as browser type, IP address, and pages visited through standard analytics tools.`,
  },
  {
    id: "use",
    title: "How We Use Your Information",
    icon: "✦",
    content: `We use the information we collect to:\n\n• Process and confirm your bookings\n• Communicate with you about your tours\n• Send you booking confirmations and receipts\n• Respond to your enquiries and support requests\n• Improve our services and website experience\n• Send promotional communications (only with your consent)\n• Comply with legal obligations\n\nWe will never sell, rent, or trade your personal data to third parties for marketing purposes.`,
  },
  {
    id: "sharing",
    title: "Information Sharing",
    icon: "✦",
    content: `We may share your information with trusted third parties only where necessary to deliver our services — for example, payment processors or booking platform partners. All third parties are required to handle your data securely and in accordance with applicable data protection law. We may also disclose information where required by law, regulation, or legal process.`,
  },
  {
    id: "storage",
    title: "Data Storage & Security",
    icon: "✦",
    content: `Your data is stored securely on protected servers. We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, loss, or misuse. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security. We retain your data only for as long as necessary to fulfil the purposes outlined in this policy.`,
  },
  {
    id: "rights",
    title: "Your Rights",
    icon: "✦",
    content: `You have the right to:\n\n• Access the personal data we hold about you\n• Request correction of inaccurate data\n• Request deletion of your data\n• Withdraw consent for marketing communications at any time\n• Lodge a complaint with a relevant data protection authority\n\nTo exercise any of these rights, please contact us at nairobisultimateexperience@gmail.com.`,
  },
  {
    id: "cookies",
    title: "Cookies",
    icon: "✦",
    content: `Our website may use cookies — small text files stored on your device — to improve your browsing experience and understand how visitors use our site. You can control cookie settings through your browser preferences. Disabling cookies may affect certain features of the website.`,
  },
  {
    id: "thirdparty",
    title: "Third-Party Links",
    icon: "✦",
    content: `Our website may contain links to third-party websites such as TripAdvisor, Instagram, and GetYourGuide. We are not responsible for the privacy practices of these platforms and encourage you to review their respective privacy policies before providing any personal information.`,
  },
  {
    id: "children",
    title: "Children's Privacy",
    icon: "✦",
    content: `Our services are not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us immediately so we can take appropriate action.`,
  },
  {
    id: "updates",
    title: "Updates to This Policy",
    icon: "✦",
    content: `We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically to stay informed about how we protect your information. Continued use of our services after changes are posted constitutes your acceptance of the revised policy.`,
  },
  {
    id: "contact",
    title: "Contact Us",
    icon: "✦",
    content: `If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:\n\nEmail: nairobisultimateexperience@gmail.com\nPhone: +254 105 057 005\nLocation: Nairobi, Kenya`,
  },
];

function PrivacyPolicy() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C97A;
          --dark: #0A0A0A;
        }

        .pp-root {
          font-family: 'Jost', sans-serif;
          background: var(--dark);
          color: #fff;
          min-height: 100vh;
        }

        /* Hero */
        .pp-hero {
          background: linear-gradient(160deg, #07100f 0%, #001a12 60%, #0A0A0A 100%);
          border-bottom: 1px solid rgba(201,168,76,0.2);
          padding: 5rem 1.5rem 3.5rem;
          text-align: center;
        }
        .pp-eyebrow {
          font-size: 0.68rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.8rem;
        }
        .pp-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.6rem;
        }
        .pp-divider {
          width: 50px;
          height: 2px;
          background: var(--gold);
          margin: 1.2rem auto;
        }
        .pp-updated {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.35);
          letter-spacing: 1px;
        }

        /* Commitment banner */
        .pp-banner {
          background: rgba(201,168,76,0.07);
          border-top: 1px solid rgba(201,168,76,0.15);
          border-bottom: 1px solid rgba(201,168,76,0.15);
          padding: 1.5rem 1.5rem;
          text-align: center;
        }
        .pp-banner p {
          max-width: 680px;
          margin: 0 auto;
          font-size: 0.88rem;
          color: rgba(255,255,255,0.6);
          line-height: 1.75;
        }
        .pp-banner strong {
          color: var(--gold-light);
        }

        /* Layout */
        .pp-body {
          max-width: 860px;
          margin: 0 auto;
          padding: 4rem 1.5rem 6rem;
        }

        /* TOC */
        .pp-toc {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(201,168,76,0.16);
          border-radius: 8px;
          padding: 2rem 2rem 1.5rem;
          margin-bottom: 3.5rem;
        }
        .pp-toc-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--gold-light);
          margin-bottom: 1rem;
        }
        .pp-toc ol {
          padding-left: 1.2rem;
          margin: 0;
          columns: 2;
          column-gap: 2rem;
        }
        .pp-toc li { margin-bottom: 0.5rem; }
        .pp-toc a {
          color: rgba(255,255,255,0.6);
          text-decoration: none;
          font-size: 0.83rem;
          transition: color 0.2s;
        }
        .pp-toc a:hover { color: var(--gold); }

        /* Sections */
        .pp-section {
          border-bottom: 1px solid rgba(255,255,255,0.06);
          padding: 2.5rem 0;
        }
        .pp-section:last-child { border-bottom: none; }

        .pp-section-header {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.2rem;
        }
        .pp-section-icon {
          color: var(--gold);
          font-size: 0.6rem;
          margin-top: 0.55rem;
          flex-shrink: 0;
        }
        .pp-section-num {
          font-size: 0.65rem;
          letter-spacing: 2px;
          color: rgba(201,168,76,0.5);
          font-weight: 500;
          text-transform: uppercase;
          display: block;
          margin-bottom: 0.2rem;
        }
        .pp-section-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.3rem, 3vw, 1.75rem);
          font-weight: 700;
          color: #fff;
          line-height: 1.15;
          margin: 0;
        }
        .pp-section-text {
          font-size: 0.93rem;
          font-weight: 300;
          line-height: 1.95;
          color: rgba(255,255,255,0.65);
          white-space: pre-line;
          padding-left: 1.6rem;
        }

        /* Rights highlight card */
        .pp-rights-card {
          background: rgba(201,168,76,0.06);
          border-left: 3px solid var(--gold);
          border-radius: 0 6px 6px 0;
          padding: 1.4rem 1.6rem;
          margin-top: 1rem;
          margin-left: 1.6rem;
        }
        .pp-rights-card li {
          font-size: 0.88rem;
          color: rgba(255,255,255,0.7);
          margin-bottom: 0.4rem;
          line-height: 1.6;
        }

        /* Footer note */
        .pp-footer-note {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(201,168,76,0.18);
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          margin-top: 4rem;
        }
        .pp-footer-note p {
          font-size: 0.88rem;
          color: rgba(255,255,255,0.6);
          margin: 0;
          line-height: 1.8;
        }
        .pp-footer-note a {
          color: var(--gold);
          text-decoration: none;
        }
        .pp-footer-note a:hover { color: var(--gold-light); }

        @media (max-width: 600px) {
          .pp-toc ol { columns: 1; }
        }
      `}</style>

      <div className="pp-root">
        {/* Hero */}
        <div className="pp-hero">
          <p className="pp-eyebrow">Legal · Nairobi's Ultimate Experience</p>
          <h1 className="pp-title">Privacy Policy</h1>
          <div className="pp-divider" />
          <p className="pp-updated">Last updated: May 2025</p>
        </div>

        {/* Commitment banner */}
        <div className="pp-banner">
          <p>
            <strong>Your privacy matters to us.</strong> We are committed to handling your
            personal data with transparency, care, and respect — in line with our values
            of precision and excellence.
          </p>
        </div>

        <div className="pp-body">
          {/* TOC */}
          <div className="pp-toc">
            <div className="pp-toc-title">Contents</div>
            <ol>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.title}</a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          {sections.map((s, i) => (
            <div key={s.id} id={s.id} className="pp-section">
              <div className="pp-section-header">
                <span className="pp-section-icon">{s.icon}</span>
                <div>
                  <span className="pp-section-num">Section {String(i + 1).padStart(2, "0")}</span>
                  <h2 className="pp-section-title">{s.title}</h2>
                </div>
              </div>
              <p className="pp-section-text">{s.content}</p>
            </div>
          ))}

          {/* Footer note */}
          <div className="pp-footer-note">
            <p>
              Questions about your data?{" "}
              <a href="mailto:nairobisultimateexperience@gmail.com">
                nairobisultimateexperience@gmail.com
              </a>
              <br />
              <a href="/terms-of-service">Read our Terms of Service →</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default PrivacyPolicy;
